import { Badge, Box, Button, Card, CardBody, Checkbox, Flex, FormControl, FormErrorMessage, FormLabel, Grid, Heading, HStack, Icon, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Radio, RadioGroup, Stack, Text, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiCheckCircle, FiPlus, FiShoppingCart, FiUser, FiUsers } from 'react-icons/fi';
import { HiCheckBadge } from 'react-icons/hi2';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formattedDate3 } from '../../../helper/utils/formattedDate';
import { doPaymentProcess } from '../../../redux/waiver/waiver.actions';

export default function Membership() {
    const toast = useToast();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [waiverData, setWaiverData] = useState(null);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [manualChildren, setManualChildren] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Form state
    const [childForm, setChildForm] = useState({
        fullname: '',
        birthdate: '',
        gender: '',
    });
    const [formErrors, setFormErrors] = useState({});

    const MEMBER_PRICE = 100000;

    useEffect(() => {
        // Load waiver data from localStorage
        try {
            const waiverString = localStorage.getItem('waiver');

            if (waiverString && waiverString !== '{}') {
                const data = JSON.parse(waiverString);
                setWaiverData(data);
            }
        } catch (error) {
            console.error('Error loading waiver data:', error);
            toast({
                title: 'Error',
                description: 'Failed to load member data',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    // Calculate age from birthdate
    const calculateAge = (birthdate) => {
        const today = new Date();
        const birthDate = new Date(birthdate);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!childForm.fullname.trim()) {
            errors.fullname = 'Full name is required';
        }

        if (!childForm.birthdate) {
            errors.birthdate = 'Birthdate is required';
        } else {
            const age = calculateAge(childForm.birthdate);
            if (age >= 17) {
                errors.birthdate = 'Child must be under 17 years old';
            }
        }

        if (!childForm.gender) {
            errors.gender = 'Gender is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle add child
    const handleAddChild = () => {
        if (!validateForm()) {
            return;
        }

        const newChild = {
            id: `manual-${Date.now()}`,
            type: 'child',
            name: childForm.fullname,
            birthdate: childForm.birthdate,
            gender: childForm.gender,
            email: '',
            phone: '',
            isManual: true,
            data: {
                name: childForm.fullname,
                birthdate: childForm.birthdate,
                gender: childForm.gender,
                statusCode: 0,
            },
        };

        setManualChildren([...manualChildren, newChild]);

        toast({
            title: 'Child added successfully',
            description: `${childForm.fullname} has been added to the list`,
            status: 'success',
            duration: 3000,
            isClosable: true,
        });

        // Reset form
        setChildForm({
            fullname: '',
            birthdate: '',
            gender: '',
        });
        setFormErrors({});
        onClose();
    };

    // Get existing members (statusCode === 1)
    const getExistingMembers = () => {
        if (!waiverData) return [];

        const members = [];

        // Check parent
        // const parentData = waiverData.data || waiverData;
        // if (parentData) {
        //     members.push({
        //         id: `parent-${parentData.memberNo || 0}`,
        //         type: 'parent',
        //         name: parentData.fullname || parentData.name || 'Parent',
        //         email: parentData.email || '',
        //         phone: parentData.phone || '',
        //         birthdate: parentData.birthDate || parentData.birthdate,
        //         memberNo: parentData.memberNo,
        //         data: parentData,
        //     });
        // }

        // Check children
        const childrenArray = waiverData.children || waiverData.child || [];

        if (Array.isArray(childrenArray) && childrenArray.length > 0) {
            childrenArray.forEach((child, index) => {
                if (child.statusCode === 1 || child.statusCode === '1') {
                    const isWaiver = +(child['isWaiver'] || '0') === 1;

                    members.push({
                        id: isWaiver ? `parent-${child.childrenID || child.id || index}` : `child-${child.childrenID || child.id || index}`,
                        type: isWaiver ? 'parent' : 'child',
                        name: child.name || (isWaiver ? 'Parent' : `Child ${index + 1}`),
                        email: child.email || '',
                        phone: child.phone || '',
                        birthdate: child.birthDate || child.birthdate,
                        gender: child.gender || null,
                        memberNo: child.memberNo || null,
                        data: child,
                    });
                }
            });
        }

        return members;
    };

    // Get eligible members (statusCode === 0 means NOT a member yet)
    const getEligibleMembers = () => {
        if (!waiverData) return [];

        const members = [];

        // Add parent/waiver from data object if eligible (statusCode 0 = not a member)
        // const parentData = waiverData.data || waiverData;
        // if (parentData && (parentData.statusCode === 0 || parentData.statusCode === '0')) {
        //     members.push({
        //         id: `parent-${parentData.memberNo || 0}`,
        //         type: 'parent',
        //         name: parentData.fullname || parentData.name || 'Parent',
        //         email: parentData.email || '',
        //         phone: parentData.phone || '',
        //         birthdate: parentData.birthDate || parentData.birthdate,
        //         data: parentData,
        //     });
        // }

        // Add children if eligible - ONLY statusCode 0 (not a member yet)
        const childrenArray = waiverData.children || waiverData.child || [];

        if (Array.isArray(childrenArray) && childrenArray.length > 0) {
            childrenArray.forEach((child, index) => {
                // ONLY include if statusCode is 0 (not a member yet)
                // statusCode 1 = already a member, so exclude
                if (child.statusCode === 0 || child.statusCode === '0') {
                    const isWaiver = +(child['isWaiver'] || '0') === 1;

                    members.push({
                        id: isWaiver ? `parent-${child.childrenID || child.id || index}` : `child-${child.childrenID || child.id || index}`,
                        type: isWaiver ? 'parent' : 'child',
                        name: child.name || (isWaiver ? 'Parent' : `Child ${index + 1}`),
                        email: child.email || '',
                        phone: child.phone || '',
                        birthdate: child.birthDate || child.birthdate,
                        gender: child.gender || null,
                        memberNo: child.memberNo || null,
                        data: child,
                    });
                }
            });
        }

        return members;
    };

    const eligibleMembers = [...getEligibleMembers(), ...manualChildren];
    const existingMembers = getExistingMembers();

    const handleToggleMember = (memberId) => {
        setSelectedMembers((prev) => {
            if (prev.includes(memberId)) {
                return prev.filter((id) => id !== memberId);
            } else {
                return [...prev, memberId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedMembers.length === eligibleMembers.length) {
            setSelectedMembers([]);
        } else {
            setSelectedMembers(eligibleMembers.map((m) => m.id));
        }
    };

    const handleProceedToCheckout = () => {
        const brand = JSON.parse(localStorage.getItem('page') || '{}')['brandCode'];
        const waiver = JSON.parse(localStorage.getItem('waiver') || '{}')['data'];

        if (selectedMembers.length === 0) {
            toast({
                title: 'No members selected',
                description: 'Please select at least one member to proceed',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Store selected members for checkout
        const selectedMembersData = eligibleMembers.filter((m) => selectedMembers.includes(m.id));
        localStorage.setItem('selectedMembership', JSON.stringify(selectedMembersData));

        toast({
            title: 'Proceeding to checkout',
            description: `${selectedMembers.length} member(s) selected`,
            status: 'success',
            duration: 2000,
            isClosable: true,
        });

        const minor = selectedMembersData.map((member, index) => {
            return {
                childrenID: member['childrenID'] || '',
                name: member['name'] || '',
                birthDate: member['birthDate'] || member['birthdate'] || '',
                gender: (member['gender'] || '') == 'male' ? '1' : '0',
                statusCode: '0',
                isWaiver: '0',
                isNew: '1',
            };
        });

        const major = {
            email: waiver?.email || '',
            address: waiver?.address || '',
            phone: waiver?.phone || '',
            gender: waiver?.gender || '',
            birthDate: waiver?.birthDate || waiver?.birthdate || '',
            id: waiver?.id || '',
            fullname: waiver?.fullname || waiver?.name || '',
        };

        const payload = {
            email: major?.email ?? '',
            parentCode: major?.id ?? '',
            major,
            minor,
            orderSummary: {
                subTotal: +totalPrice,
            },
            isMember: '1',
            brand: brand,
        };

        dispatch(doPaymentProcess(payload, navigate));
    };

    const totalPrice = selectedMembers.length * MEMBER_PRICE;

    if (isLoading) {
        return (
            <Box textAlign='center' py={20}>
                <Text color='gray.500'>Loading membership data...</Text>
            </Box>
        );
    }

    if (eligibleMembers.length === 0 && !waiverData) {
        // Only show this if no waiver data at all
        return (
            <VStack spacing={6} align='stretch' w='full'>
                <Box mt={{ base: 5, md: 0 }} mb={3}>
                    <Heading size={{ base: 'md', md: 'lg' }} color='gray.800' mb={{ base: 0, md: 2 }} textAlign={{ base: 'center', md: 'left' }}>
                        Membership Registration
                    </Heading>
                    <Text color='gray.600' fontSize={{ base: 'sm', md: 'md' }} textAlign={{ base: 'center', md: 'left' }}>
                        Register as a member to get exclusive benefits
                    </Text>
                </Box>

                <Box bg='white' borderRadius='2xl' p={{ base: 8, md: 12 }} border='1px' borderColor='gray.100' textAlign='center'>
                    <Icon as={FiCheckCircle} fontSize='6xl' color='green.500' mb={4} />
                    <Heading size='md' color='gray.800' mb={2}>
                        All Set!
                    </Heading>
                    <Text color='gray.600' fontSize='md'>
                        All eligible members are already registered or no eligible members found.
                    </Text>
                </Box>
            </VStack>
        );
    }

    return (
        <VStack spacing={{ base: 4, md: 6 }} align='stretch' w='full'>
            {/* Header */}
            <Box mt={{ base: 5, md: 0 }} mb={3}>
                <Heading size={{ base: 'md', md: 'lg' }} color='gray.800' textAlign={{ base: 'center', md: 'left' }}>
                    Membership Registration
                </Heading>
                <Text color='gray.600' fontSize={{ base: 'sm', md: 'md' }} textAlign={{ base: 'center', md: 'left' }}>
                    Select members to register and enjoy exclusive benefits
                </Text>
            </Box>

            {/* Pricing Info Card */}
            <Card bg='gradient-to-r' bgGradient='linear(to-r, purple.500, pink.500)' color='white' borderRadius='2xl'>
                <CardBody>
                    <Flex direction={{ base: 'column', md: 'row' }} justify='space-between' align={{ base: 'start', md: 'center' }} gap={4}>
                        <VStack align='start' spacing={1}>
                            <Text opacity={0.9} fontSize='md' fontWeight={600}>
                                Membership Price
                            </Text>
                            <Heading size='xl'>Rp {MEMBER_PRICE.toLocaleString('id-ID')}</Heading>
                            <Text fontSize='sm' opacity={0.9}>
                                per member
                            </Text>
                        </VStack>
                    </Flex>
                </CardBody>
            </Card>

            {/* Select All & Add Child Button */}
            <Flex justify='space-between' align='center' px={2} flexWrap='wrap' gap={3}>
                <Text fontSize='md' fontWeight='semibold' color='gray.700'>
                    Create New Members ({eligibleMembers.length})
                </Text>
                <HStack spacing={3}>
                    <Button size='sm' leftIcon={<FiPlus />} colorScheme='pink' onClick={onOpen}>
                        Add Child
                    </Button>
                    <Button size='sm' variant='ghost' colorScheme='primary' onClick={handleSelectAll}>
                        {selectedMembers.length === eligibleMembers.length ? 'Deselect All' : 'Select All'}
                    </Button>
                </HStack>
            </Flex>

            {/* Members Grid */}
            {eligibleMembers.length === 0 ? (
                <Box bg='white' borderRadius='2xl' p={{ base: 8, md: 12 }} border='1px' borderColor='gray.100' textAlign='center'>
                    <Icon as={FiUsers} fontSize='6xl' color='gray.400' mb={4} />
                    <Heading size='md' color='gray.800' mb={2}>
                        No Members Yet
                    </Heading>
                    <Text color='gray.600' fontSize='md' mb={4}>
                        Add a child to get started with membership registration
                    </Text>
                    <Button leftIcon={<FiPlus />} colorScheme='primary' onClick={onOpen}>
                        Add Your Child
                    </Button>
                </Box>
            ) : (
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={{ base: 4, md: 6 }}>
                    {eligibleMembers.map((member) => {
                        const isSelected = selectedMembers.includes(member.id);

                        return (
                            <Card
                                key={member.id}
                                bg='white'
                                borderRadius='2xl'
                                border='2px'
                                borderColor={isSelected ? 'primary.500' : 'gray.100'}
                                cursor='pointer'
                                onClick={() => handleToggleMember(member.id)}
                                _hover={{
                                    transform: 'translateY(-4px)',
                                    borderColor: 'primary.300',
                                    shadow: 'md',
                                }}
                                transition='all 0.3s'
                                position='relative'
                                overflow='hidden'>
                                {isSelected && (
                                    <Box position='absolute' top={0} right={0} bg='primary.500' color='white' px={3} py={1} borderBottomLeftRadius='lg'>
                                        <Icon as={FiCheckCircle} />
                                    </Box>
                                )}

                                <CardBody p={6}>
                                    <VStack spacing={4} align='start'>
                                        {/* Icon & Type Badge */}
                                        <Flex justify='space-between' w='full' align='start'>
                                            <Flex w='60px' h='60px' bg={member.type === 'parent' ? 'primary.100' : 'pink.100'} borderRadius='xl' align='center' justify='center' color={member.type === 'parent' ? 'primary.600' : 'pink.600'}>
                                                <Icon as={member.type === 'parent' ? FiUser : FiUsers} fontSize='2xl' />
                                            </Flex>
                                            <Flex alignItems='center' gap={2}>
                                                <Badge colorScheme={member.type === 'parent' ? 'primary' : 'pink'} borderRadius='full' px={3}>
                                                    {member.type === 'parent' ? 'Parent' : 'Child'}
                                                </Badge>
                                                {member.isManual && (
                                                    <Badge colorScheme='green' borderRadius='full' px={2} fontSize='xs'>
                                                        Manual
                                                    </Badge>
                                                )}
                                            </Flex>
                                        </Flex>

                                        {/* Member Info */}
                                        <VStack spacing={1} align='start' w='full'>
                                            <Text fontSize='lg' fontWeight='bold' color='gray.800' noOfLines={1}>
                                                {member.name}
                                            </Text>
                                            {member.birthdate && (
                                                <Text fontSize='sm' color='gray.600'>
                                                    Age: {calculateAge(member.birthdate)} years
                                                </Text>
                                            )}
                                            {/* {member.gender && (
                                                <Text fontSize='sm' color='gray.600'>
                                                    {member.gender === 'male' ? '👦 Male' : '👧 Female'}
                                                </Text>
                                            )} */}
                                            {member.email && (
                                                <Text fontSize='sm' color='gray.600' noOfLines={1}>
                                                    {member.email}
                                                </Text>
                                            )}
                                            {member.phone && (
                                                <Text fontSize='sm' color='gray.600'>
                                                    {member.phone}
                                                </Text>
                                            )}
                                        </VStack>

                                        {/* Checkbox */}
                                        <Checkbox isChecked={isSelected} colorScheme='primary' size='lg' w='full' onChange={() => handleToggleMember(member.id)} onClick={(e) => e.stopPropagation()}>
                                            <Text fontSize='sm' fontWeight='semibold' color='gray.700'>
                                                Select for membership
                                            </Text>
                                        </Checkbox>
                                    </VStack>
                                </CardBody>
                            </Card>
                        );
                    })}
                </Grid>
            )}

            {/* Checkout Summary */}
            {selectedMembers.length > 0 && (
                <Card w='full' bg='white' borderRadius='2xl' border='1px' borderColor='gray.100' position='sticky' bottom={4} shadow='xl'>
                    <CardBody>
                        <Flex direction={{ base: 'column', md: 'row' }} justify='space-between' align={{ base: 'stretch', md: 'center' }} gap={7}>
                            <VStack align='start' spacing={1}>
                                <Text fontSize='sm' color='gray.600'>
                                    Selected Members
                                </Text>
                                <Heading size='md' color='gray.800'>
                                    {selectedMembers.length} {selectedMembers.length === 1 ? 'Member' : 'Members'}
                                </Heading>
                                <Text fontSize='lg' fontWeight='bold' color='primary.600'>
                                    Total: Rp {totalPrice.toLocaleString('id-ID')}
                                </Text>
                            </VStack>

                            <Button colorScheme='primary' size='lg' fontSize='md' leftIcon={<FiShoppingCart />} onClick={handleProceedToCheckout} w={{ base: 'full', md: 'auto' }} px={8}>
                                Checkout
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>
            )}

            {/* Existing Members Section */}
            {existingMembers.length > 0 && (
                <VStack spacing={4} align='stretch'>
                    <Flex justify='space-between' align='center' px={2}>
                        <HStack spacing={2}>
                            <Icon as={HiCheckBadge} fontSize='xl' color='green.500' />
                            <Text fontSize='md' fontWeight='semibold' color='gray.700'>
                                Active Members ({existingMembers.length})
                            </Text>
                        </HStack>
                    </Flex>

                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={{ base: 4, md: 6 }}>
                        {existingMembers.map((member) => {
                            const data = member['data'] || {};
                            const createdAt = data['expDate'] || data['created_at'] || '';

                            // Calculate expiration date (created_at + 1 year)
                            let expirationDate = null;
                            let daysUntilExpiration = 0;
                            let isActive = false;
                            let expirationText = '';

                            if (createdAt) {
                                const created = new Date(createdAt);
                                expirationDate = new Date(created);
                                // expirationDate.setFullYear(expirationDate.getFullYear() + 1);

                                const today = new Date();
                                const diffTime = expirationDate - today;
                                daysUntilExpiration = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                isActive = daysUntilExpiration > 0;

                                if (isActive) {
                                    expirationText = `${daysUntilExpiration} Day Left'${daysUntilExpiration !== 1 ? 's' : ''}`;
                                } else {
                                    expirationText = `Expired ${Math.abs(daysUntilExpiration)} day${Math.abs(daysUntilExpiration) !== 1 ? 's' : ''} ago`;
                                }
                            }

                            return (
                                <Card key={member.id} bg='white' borderRadius='2xl' border='2px' borderColor={isActive ? 'green.200' : 'red.200'} position='relative' overflow='hidden'>
                                    {/* Member Badge */}
                                    <Box position='absolute' top={0} right={0} bg={isActive ? 'green.500' : 'red.500'} color='white' px={3} py={1} borderBottomLeftRadius='lg'>
                                        <HStack spacing={1}>
                                            <Icon as={HiCheckBadge} fontSize='sm' />
                                            <Text fontSize='xs' fontWeight='bold'>
                                                {isActive ? 'ACTIVE' : 'EXPIRED'}
                                            </Text>
                                        </HStack>
                                    </Box>

                                    <CardBody p={6}>
                                        <VStack spacing={4} align='start'>
                                            {/* Icon & Type Badge */}
                                            <Flex justify='space-between' w='full' align='start'>
                                                <Flex w='60px' h='60px' bg={isActive ? 'green.100' : 'red.100'} borderRadius='xl' align='center' justify='center' color='green.600'>
                                                    <Icon as={member.type === 'parent' ? FiUser : FiUsers} fontSize='2xl' />
                                                </Flex>
                                                <Badge colorScheme={member.type === 'parent' ? 'green' : 'blue'} borderRadius='full' px={3} mt={2}>
                                                    {member.type === 'parent' ? 'Parent' : 'Child'}
                                                </Badge>
                                            </Flex>

                                            {/* Member Info */}
                                            <VStack spacing={1} align='start' w='full'>
                                                <Text fontSize='lg' fontWeight='bold' color='gray.800' noOfLines={1}>
                                                    {member.name}
                                                </Text>
                                                {member.birthdate && (
                                                    <Text fontSize='sm' color='gray.600'>
                                                        Age: {calculateAge(member.birthdate)} years
                                                    </Text>
                                                )}
                                                <Text fontSize='sm' color='gray.600'>
                                                    Expired at {formattedDate3(expirationDate)} ( {expirationText})
                                                </Text>
                                                {member.memberNo && (
                                                    <Text fontSize='sm' color='green.600' fontWeight='semibold'>
                                                        Member #{member.memberNo}
                                                    </Text>
                                                )}
                                                {/* {member.gender && (
                                                        <Text fontSize='sm' color='gray.600'>
                                                            {member.gender === 'male' ? '👦 Male' : '👧 Female'}
                                                        </Text>
                                                    )} */}
                                            </VStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </Grid>
                </VStack>
            )}

            {/* Add Child Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size='md' isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Add Child Manually</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <VStack spacing={4}>
                            <FormControl isInvalid={formErrors.fullname}>
                                <FormLabel>Full Name</FormLabel>
                                <Input placeholder='Enter child full name' value={childForm.fullname} onChange={(e) => setChildForm({ ...childForm, fullname: e.target.value })} />
                                <FormErrorMessage>{formErrors.fullname}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={formErrors.birthdate}>
                                <FormLabel>Birthdate</FormLabel>
                                <Input type='date' value={childForm.birthdate} max={new Date().toISOString().split('T')[0]} onChange={(e) => setChildForm({ ...childForm, birthdate: e.target.value })} />
                                <FormErrorMessage>{formErrors.birthdate}</FormErrorMessage>
                                {childForm.birthdate && !formErrors.birthdate && (
                                    <Text fontSize='sm' color='green.600' mt={1}>
                                        Age: {calculateAge(childForm.birthdate)} years old
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl isInvalid={formErrors.gender}>
                                <FormLabel>Gender</FormLabel>
                                <RadioGroup value={childForm.gender} onChange={(value) => setChildForm({ ...childForm, gender: value })}>
                                    <Stack direction='row' spacing={5}>
                                        <Radio value='male' colorScheme='primary'>
                                            👦 Male
                                        </Radio>
                                        <Radio value='female' colorScheme='pink'>
                                            👧 Female
                                        </Radio>
                                    </Stack>
                                </RadioGroup>
                                <FormErrorMessage>{formErrors.gender}</FormErrorMessage>
                            </FormControl>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant='ghost' mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button colorScheme='primary' onClick={handleAddChild}>
                            Add Child
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
}
